import { Component, Element, h, Listen, Prop } from '@stencil/core';
import { ButtonType, CssClassMap } from '../globals';

@Component({
  tag: 'yth-button',
  styleUrl: 'yth-button.scss',
  shadow: true,
})
export class YthButton {
  /**
   * The variant of the button; i.e. `fill`, `outline` or `text`.
   */
  @Prop({ reflect: true }) variant?: 'fill' | 'outline' | 'text' = 'fill';

  /**
   * The color of the button.
   */
  @Prop({ reflect: true }) color?: 'primary' | 'attention' = 'primary';

  /**
   * Controls the color of the button when the `variant` selected is `'text'`.
   */
  @Prop({ reflect: true }) textColor?: 'default' | 'secondary' | 'dark' =
    'default';

  /**
   * The size of the button.
   */
  @Prop({ reflect: true }) size?: 'small' | 'base' | 'large' = 'base';

  /**
   * Ignores the button padding by setting negative margins on the button that
   * is equal to its padding counterparts. You can focus these negative margins
   * on the sides that need them or all around.
   */
  @Prop({ reflect: true }) pull?: Pull = 'none';

  /**
   * Whether this button is disabled.
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * Whether this button is a loader.
   */
  @Prop({ reflect: true }) loader?: boolean = false;

  /**
   * Whether this button should automatically become a loader when
   * clicked.
   */
  @Prop({ reflect: true }) autoClickLoader?: boolean = false;

  /**
   * Whether this button should fill the width with its container.
   */
  @Prop({ reflect: true }) expanded?: boolean = false;

  /**
   * The download attribute specifies that the target will be downloaded when
   * a user clicks on the hyperlink. This is only applicable to links with an
   * `href` attribute.
   */
  @Prop({ reflect: true }) download?: boolean = false;

  /**
   * The type of button to render.
   */
  @Prop() type?: ButtonType = 'button';

  /**
   * The value of the button. When a value is supplied, it will be posted in
   * the form if it has a `type` of `submit`.
   */
  @Prop() value?: string = null;

  /**
   * The href to go to. If this property is supplied then a `<a>` tag
   * will be generated and not a `<button>`.
   */
  @Prop() href?: string = null;

  /**
   * Applicable only to href, allows the target to be passed in.
   */
  @Prop() target?: string = null;

  /**
   * The icon to show on the button.
   */
  @Prop() icon?: Icon = null;

  /**
   * When an Icon is selected on the button this determines whether the button
   * should be appended or prepended to the button text.
   */
  @Prop() iconLocation?: 'start' | 'end' = 'start';

  /**
   * If onclick for submit buttons should auto submit.
   */
  @Prop({ reflect: true }) disableAutoSubmit?: boolean = false;

  @Element() el: HTMLElement;

  private buttonEl?: HTMLButtonElement | HTMLAnchorElement;

  form: HTMLFormElement;

  handleOnClick = (event: MouseEvent) => {
    // We want to unfocus the button after we have clicked it.
    this.buttonEl?.blur();

    if (this.loader || this.disabled) {
      event.preventDefault();
      return;
    } else if (this.autoClickLoader) {
      this.loader = true;
    }

    if (this.type === 'submit' && !this.disableAutoSubmit) {
      this.form.submit();
    }
  };

  render() {
    const cssClassMap = this.getCssClassMap();

    return this.href != null
      ? this.renderLink(cssClassMap)
      : this.renderButton(cssClassMap);
  }

  renderLink(cssClassMap: CssClassMap) {
    return (
      <a
        href={this.href}
        class={cssClassMap}
        onClick={this.handleOnClick}
        download={this.download ? this.download : undefined}
        ref={el => (this.buttonEl = el as HTMLAnchorElement)}
        role="button"
        target={this.target}
      >
        {this.renderContent()}
      </a>
    );
  }

  renderButton(cssClassMap: CssClassMap) {
    return (
      <button
        type={this.type}
        class={cssClassMap}
        disabled={this.disabled}
        onClick={this.handleOnClick}
        ref={el => (this.buttonEl = el as HTMLButtonElement)}
        role="button"
      >
        {this.renderContent()}
      </button>
    );
  }

  renderIcon() {
    return (
      <yth-icon
        class="icon"
        icon={this.icon}
        size={this.size === 'large' ? 'xl' : 'base'}
      />
    );
  }

  renderContent() {
    return this.loader ? (
      <span class="loader"></span>
    ) : (
      [
        this.icon && this.iconLocation === 'start' && this.renderIcon(),
        <slot></slot>,
        this.icon && this.iconLocation === 'end' && this.renderIcon(),
      ]
    );
  }

  @Listen('pagehide', { target: 'window' })
  async onPageHide() {
    this.loader = false;
  }

  componentDidLoad() {
    this.form = this.el.closest('form');
  }

  private getCssClassMap(): CssClassMap {
    return {
      [this.variant]: true,
      [this.color]: true,
      [`text-${this.textColor}`]: true,
      [this.size]: this.size != 'base',
      ['expanded']: this.expanded,
      ['disabled']: this.disabled && this.href != null,
      ['loader']: this.loader,
      ['pull-top']: this.pull == 'all' || this.pull.includes('top'),
      ['pull-right']: this.pull == 'all' || this.pull.includes('right'),
      ['pull-bottom']: this.pull == 'all' || this.pull.includes('bottom'),
      ['pull-left']: this.pull == 'all' || this.pull.includes('left'),
    };
  }
}
